import { Airplane, CheckCircle, Clock, Package } from "@phosphor-icons/react";
import { FC, useMemo } from "react";
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from "react-circular-progressbar";
import { Link } from "react-router-dom";
import { Installment } from "../@types";
import colors from "../assets/colors";
import { getStatusColor } from "../helpers/colors";
import { formatAsMoney } from "../helpers/numbers";
import moment from "moment";

/** Format payment due date; supports Alert format (e.g. "2/7/2025 12:00:00 AM") and ISO. */
function formatPaymentDue(dateStr: string): string {
  if (!dateStr || typeof dateStr !== "string") return "—";
  const trimmed = dateStr.trim();
  const parsed =
    moment(trimmed, "M/D/YYYY h:mm:ss A", true).isValid()
      ? moment(trimmed, "M/D/YYYY h:mm:ss A")
      : moment(trimmed, moment.ISO_8601, true).isValid()
        ? moment(trimmed)
        : null;
  return parsed?.isValid() ? parsed.format("DD MMM") : "—";
}

const LoanItem: FC<{
  installment: Installment;
}> = ({ installment }) => {
  const paidCount = useMemo(
    () => installment.installments.filter((i) => i.paid).length,
    [installment.installments]
  );
  const completed = useMemo(() => {
    return (
      installment.number_of_installments > 0 &&
      paidCount ===
      installment.number_of_installments
    );
  }, [installment.number_of_installments, paidCount]);
  return (
    <Link
      to={`${installment.loan.id}`}
      className="flex items-center justify-between p-5 rounded-xl bg-white shadow-sm cursor-pointer"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="w-12 aspect-square">
          {installment.loan_status === "pending" ? (
            <div className="aspect-square bg-yellow-50 rounded-full flex items-center justify-center">
              <Clock size={44} weight="duotone" className="text-yellow-500" />
            </div>
          ) : completed ? (
            <div className="aspect-square bg-green-50 rounded-full flex items-center justify-center">
              <CheckCircle size={44} weight="fill" className="text-green-500" />
            </div>
          ) : (
            <CircularProgressbarWithChildren
              styles={buildStyles({
                pathColor: completed ? "#22c55e" : colors.ORANGE_01,
                strokeLinecap: "round",
                trailColor: "#EEDAD8",
              })}
              strokeWidth={10}
              value={paidCount}
              maxValue={installment.number_of_installments}
            >
              <div
                data-completed={completed}
                className="aspect-square p-2 text-visaro-orange data-[completed=true]:text-green-500"
              >
                {installment.service === "AVIATION" ? (
                  <Airplane size={20} />
                ) : (
                  <Package size={20} />
                )}
              </div>
            </CircularProgressbarWithChildren>
          )}
        </div>
        <div>
          <div className="text-lg font-medium capitalize">
            Pay in 3 loan for {installment.loan.industry}
          </div>
          {installment.loan_status === "pending" ? (
            <div className="text-sm text-visaro-orange font-medium">
              This loan request is yet to be approved
            </div>
          ) : (
            <div className="text-sm text-gray-500 font-medium">
              {paidCount} of {installment.number_of_installments} paid
            </div>
          )}
        </div>
      </div>
      {installment.loan_status === "pending" ? (
        <div className="flex gap-4 items-center">
          <div
            style={{
              backgroundColor: getStatusColor(installment.loan_status).bg,
              color: getStatusColor(installment.loan_status).text,
            }}
            className="bg-green-100 text-sm capitalize w-max px-3 py-1 rounded-full flex items-center gap-1 text-green-700"
          >
            <span>{installment.loan_status}</span>
          </div>
          <div className="font-medium text-xl">
            ₦ {formatAsMoney(installment.loan.loan_amount)}
          </div>
        </div>
      ) : (
        <div className="text-right">
          <div className="font-medium text-lg">
            ₦ {formatAsMoney(installment.loan.per_installment_amount)}
          </div>
          <div className="text-sm text-gray-500 font-medium">
            Payment due {formatPaymentDue(installment.next_payment_date)}
          </div>
        </div>
      )}
    </Link>
  );
};

export default LoanItem;
